import { get } from '../Services/http';
import { catchError } from './Error.action';
import { setCookie } from '../Components/CookieHandler';
import {
    FACETED_SEARCH_QUERY,
    FACETED_SEARCH_RECEIVE,
    FACETED_SEARCH_ERROR,
    FACETED_SEARCH_TOGGLE_VALUE,
    FACETED_SEARCH_TOGGLE_COMPACT,
    PRODUCT_VIEW_CACHED,
    RESET_RANGE_FILTERS,
} from '../constants';

export const query = (
    queryString = '',
    withHtmlResult = true,
    productsViewCachedId = new Date().getTime() + '',
    resetRangeFilter = '',
) => (dispatch, getState) => {
    let url = withHtmlResult
        ? '/api/productFilter/withHtmlResult'
        : '/api/productFilter';
    if (queryString && queryString.trim() !== '') {
        url += `?${queryString}`;
    }
    return get(url)
        .then(response => response.json())
        .then((result) => {
            const {
                productsView,
                sortCriteria,
                subNavigation,
                stickyFilterQuery,
                ...others
            } = result;
            if (withHtmlResult) {
                window.__litium.cache[PRODUCT_VIEW_CACHED] = {
                    productsViewCachedId,
                    productsView,
                };
                setCookie(
                    'stickyfilterquery',
                    encodeURIComponent(stickyFilterQuery),
                );
            }
            result = {
                ...others,
                sortCriteria,
                subNavigation,
                productsViewCachedId,
            };
            dispatch(receive(result, resetRangeFilter));
        })
        .catch((ex) => dispatch(catchError(ex, (error) => searchError(error))));
};

const submit = (facetFilters, resetRangeFilter) => (dispatch, getState) => {
    const filterCriteria = toFilterCriteria(facetFilters);
    const filterIds = facetFilters.map((filter) => filter.id);
    const ignoredParams = ['page'];
    const unChangedParams = (window.location.search.substr(1) || '')
        .split('&')
        .filter((param) => {
            const [id, value] = param.split('=');
            return (
                param.length > 0 &&
                !filterIds.includes(id) &&
                !ignoredParams.includes(id)
            );
        });
    const q = [...unChangedParams, ...filterCriteria].join('&');
    dispatch(saveQuery(q));
    return dispatch(
        query(q, true, new Date().getTime() + '', resetRangeFilter),
    );
};

const toFilterCriteria = groups => {
    let filterCriteria = groups
        .filter(group => !group.isSlider)
        .map(group =>
            !group.isSlider
                ? group.selectedOptions
                    .filter(val => val && val.length > 0)
                    .map(
                        val =>
                            `${encodeURIComponent(
                                group.id,
                            )}=${encodeURIComponent(val)}`,
                    )
                : `${encodeURIComponent(group.id)}=${encodeURIComponent(
                    group.selectedValues.min,
                )}&${encodeURIComponent(group.id)}=${encodeURIComponent(
                    group.selectedValues.max,
                )}`,
        )
        .reduce((flat, current) => [...flat, ...current], []);

    groups
        .filter(group => group.isSlider && group.selectedOptions?.length == 2)
        .map(group =>
            filterCriteria.push(
                `${encodeURIComponent(group.id)}=${encodeURIComponent(
                    group.selectedOptions[0],
                )}&${encodeURIComponent(group.id)}=${encodeURIComponent(
                    group.selectedOptions[1],
                )}`,
            ),
        );

    return filterCriteria;
};

/**
 *
 * @param filter : the group has changed facet
 * @param option : changed facet
 */
export const searchFacetChange = (filter = null, option = null) => (
    dispatch,
    getState
) => {
    if (filter) {
        const allFilters = getState().facetedSearch.facetFilters;
        const newFilters = updateFilterOption(allFilters, filter, option);
        return dispatch(submit(newFilters));
    }
};

/**
 *
 * @param filter : the group has changed facet
 * @param minValue : slider min value
 * @param maxValue : slider max value
 */
export const searchSliderFacetChange = (
    filter = null,
    minValue = 0,
    maxValue = 9999,
) => (dispatch, getState) => {
    if (filter) {
        const allFilters = getState().facetedSearch.facetFilters;
        const newFilters = updateFilterOption(allFilters, filter, {
            min: minValue,
            max: maxValue,
        });
        return dispatch(submit(newFilters));
    }
};

/**
 *
 * @param filter : the group has changed facet
 * @param option : changed facet
 */
export const searchFacetClearGroupChange = (filter = null, option = null) => (
    dispatch,
    getState,
) => {
    if (filter) {
        const allFilters = getState().facetedSearch.facetFilters;
        const newFilters = updateFilterOption(allFilters, filter, option, true);
        return dispatch(
            submit(newFilters, filter.isSlider ? filter.id : undefined),
        );
    }
};
export const updateFilterOption = (
    allFilters = [],
    filter = null,
    option = null,
    clearGroup = false,
) => {
    if (!filter) return allFilters;
    const filterIndex = allFilters.findIndex(f => f.id === filter.id);
    if (filter.isSlider) {
        const newFilter = {
            ...filter,
            selectedOptions: !clearGroup ? [option.min, option.max] : [],
        };
        const newFilters = [
            ...allFilters.slice(0, filterIndex),
            newFilter,
            ...allFilters.slice(filterIndex + 1),
        ];

        return newFilters;
    }

    const newFilter = option
        ? toggleFilterValue(filter, option, clearGroup)
        : { ...filter, selectedOptions: [] };
    const newFilters = [
        ...allFilters.slice(0, filterIndex),
        newFilter,
        ...allFilters.slice(filterIndex + 1),
    ];
    return newFilters;
};

const toggleFilterValue = (filter, option, clearGroup) => {
    const { singleSelect, selectedOptions } = filter;

    const optionIndex = selectedOptions.indexOf(option.id);
    const newSelectedOptions =
        optionIndex < 0
            ? // not yet selected, select it
              singleSelect
                ? [option.id]
                : [...selectedOptions, option.id]
            : // selected, deSelect  it
              [
                  ...selectedOptions.slice(0, optionIndex),
                  ...selectedOptions.slice(optionIndex + 1),
            ];

    return {
        ...filter,
        selectedOptions: !clearGroup ? newSelectedOptions : [],
    };
};

export const submitSearchFacet = (allFilters) => (dispatch, getState) => {
    dispatch(facetClientChanged(allFilters));
    return dispatch(submit(allFilters));
};

export const facetClientChanged = (facetFilters) => ({
    type: FACETED_SEARCH_TOGGLE_VALUE,
    payload: {
        facetFilters,
    },
});

export const toggleVisibleDropdownMenu = ({ id }) => ({
    type: FACETED_SEARCH_TOGGLE_COMPACT,
    payload: {
        visibleDropdownMenu: id,
    },
});

export const searchError = (error) => ({
    type: FACETED_SEARCH_ERROR,
    payload: {
        error,
    },
});

export const receive = (payload, filterToReset) => {
    return {
        type: FACETED_SEARCH_RECEIVE,
        payload: {
            ...payload,
            resetRangeFilter: filterToReset,
        },
    };
};

export const saveQuery = (queryString) => ({
    type: FACETED_SEARCH_QUERY,
    payload: {
        query: queryString,
    },
});

export const resetRangeFilter = filterToReset => ({
    type: RESET_RANGE_FILTERS,
    payload: {
        resetRangeFilter: filterToReset,
    },
});
